import React, { useEffect, useState } from "react";
import { X, Loader, AlertCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Turn an axios failure into something a user can act on. `detail` can be a
 * string (our HTTPExceptions) or a list of objects (FastAPI validation errors),
 * and a request that never reached the server has no `response` at all — that
 * last case used to surface as a bare "Failed to start checkout" with no clue
 * whether it was the network or the plan.
 */
export const checkoutErrorMessage = (err) => {
  if (!err?.response) return "Couldn't reach the server. Check your connection and try again.";
  const detail = err.response.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  return `Failed to start checkout (error ${err.response.status}).`;
};

/**
 * Stripe Checkout launcher modal.
 *
 * NOTE: Stripe's hosted Checkout cannot be embedded in an iframe — Stripe sets
 * X-Frame-Options: DENY and frame-ancestors: 'none' on every checkout URL by
 * design. So we briefly show a "Redirecting to secure checkout…" splash, hit
 * POST /api/payments/checkout to mint a session URL, then top-level redirect
 * the browser. After payment, Stripe sends the user to success_url
 * (`${origin}/pricing?session_id=...`) where PricingPage polls
 * /api/payments/status until the transaction is marked paid.
 *
 * When no plan is passed, this member-feature paywall deliberately chooses the
 * Member plan. Fan unlocks YSVLive but not games, clips, or highlights here.
 */
const StripeCheckoutModal = ({ onClose, planId }) => {
  const [status, setStatus] = useState("loading"); // loading | error
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("ysv_token");
    if (!token) { setError("Not authenticated"); setStatus("error"); return; }
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    let cancelled = false;

    const resolvePlanId = async () => {
      if (planId) return planId;
      const r = await axios.get(`${API}/subscription/plans`, auth);
      const plans = Array.isArray(r.data) ? r.data : [];
      const monthly = plans.find(p => p.interval === "month" && p.tier === "member");
      return monthly?.id || "ysv_member_monthly";
    };

    (async () => {
      try {
        const chosen = await resolvePlanId();
        const r = await axios.post(`${API}/payments/checkout`,
          { plan_id: chosen, origin_url: window.location.origin }, auth);
        if (cancelled) return;
        if (!r.data?.url) {
          setError("Checkout session did not return a URL");
          setStatus("error");
          return;
        }
        // Top-level redirect — Stripe blocks iframes.
        window.location.href = r.data.url;
      } catch (err) {
        if (cancelled) return;
        setError(checkoutErrorMessage(err));
        setStatus("error");
      }
    })();

    return () => { cancelled = true; };
  }, [planId]);

  return (
    <div data-testid="stripe-checkout-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: 16,
    }}>
      <div style={{
        width: 'min(96vw, 440px)',
        background: '#0e0e12', border: '1px solid var(--dark-border)',
        borderRadius: 16, overflow: 'hidden',
        boxShadow: '0 30px 60px rgba(0,0,0,0.8)',
      }}>
        <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--dark-border)' }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Outfit', sans-serif", color: '#fff' }}>Subscribe to YSV</h3>
            <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>Secure payment · Powered by Stripe</p>
          </div>
          <button data-testid="stripe-modal-close" onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: 32, color: '#fff', textAlign: 'center' }}>
          {status === "loading" && (
            <div data-testid="stripe-modal-loading">
              <Loader size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--cyan)', marginBottom: 14 }} />
              <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Redirecting to secure checkout…</p>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>You'll be brought back to YSV automatically when payment completes.</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}
          {status === "error" && (
            <div data-testid="stripe-modal-error">
              <AlertCircle size={32} style={{ color: '#f44336', marginBottom: 14 }} />
              <p style={{ fontSize: 14, color: '#f44336', marginBottom: 16 }}>{error}</p>
              <button onClick={onClose} className="btn-primary" style={{ padding: '10px 22px' }}>Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripeCheckoutModal;
